import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { describeApiError } from "../../api/errors";
import { useSelectXeroTenant, useStartXeroAuthorization, useXeroHandoff } from "../../api/hooks";
import { Banner } from "../../components/Banner";
import { Button } from "../../components/Button";
import { Field } from "../../components/Field";
import { Spinner } from "../../components/Spinner";
import { useToast } from "../../components/Toast";
import { oauthDeniedMessage } from "../../i18n/oauthCopy";
import { t } from "../../i18n/t";
import { clearDraft, readDraft, saveDraft } from "./newMigrationDraft";
import { ReturnPage } from "./ReturnPage";

/**
 * §B2.1's return route (`/oauth/xero/return`), redesigned as XeroOrg.html — a chrome-less page (no top bar;
 * `router.tsx` mounts it under `RequireSession` only). The callback redirected here with `?handoff=<id>` or
 * `?error=`.
 *
 * **Never a blank screen.** There are four states and every one of them says something: an error code, a missing
 * handoff, an expired handoff (the `404` §A6.4 answers for a consumed, expired or wrong-operator id — it leaks
 * nothing on purpose, so the client renders the same sentence as an invalid state), and the tenant list.
 *
 * The handoff id is **not** the OAuth `state` (§A6.3): the single-use OAuth credential never reaches this URL,
 * this history entry or a `Referer`. Nothing here reads a token, because the endpoint never returns one.
 *
 * **Xero-only, by design (Package W2c).** QBO/FreeAgent/Sage BC never land here: their callbacks build the
 * `Connection` row directly and always redirect to `/connections?connected=<system>` regardless of any
 * `redirect_path` (`_connections_return` in `api/routers/connections.py`), because none of the three needs a
 * tenant-picker the way a Xero authorization can cover more than one organisation. This screen stays named for
 * Xero, not renamed to something generic, because it is the one system that still needs everything below it.
 */
export function XeroReturn(): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const [params] = useSearchParams();
  const handoffId = params.get("handoff");
  const errorCode = params.get("error");
  const handoff = useXeroHandoff(errorCode === null ? handoffId : null);
  const select = useSelectXeroTenant(handoffId ?? "");
  const authorize = useStartXeroAuthorization();
  const [tenantId, setTenantId] = useState<string | null>(null);
  // The design's "Name for this connection" field, pre-filled from the draft when the operator named the
  // connection before the round trip; omitted from the POST when left empty (the server names the connection
  // after the Xero organisation picked).
  const [displayName, setDisplayName] = useState(() => readDraft().xero_display_name ?? "");
  const [failure, setFailure] = useState<string | null>(null);

  if (errorCode !== null) {
    // F6: Xero's own `error` (RFC 6749 §4.1.2.1) travels through as `oauth_error` exactly like the other
    // vendors' callbacks (`connections.py::_denied_query`); `system` is always "xero" here since this
    // route is Xero-only (see the module docstring above).
    return (
      <ReturnPage sourceSystem="xero" title={t("oauth.return_title")}>
        <Banner tone="warn">
          {errorCode === "oauth_denied"
            ? oauthDeniedMessage("xero", params.get("oauth_error"))
            : t("oauth.state_invalid")}
        </Banner>
        <div>
          <Button onClick={() => navigate("/migrations/new")}>{t("oauth.start_again")}</Button>
        </div>
      </ReturnPage>
    );
  }

  if (handoffId === null) {
    return (
      <ReturnPage sourceSystem="xero" title={t("oauth.return_title")}>
        <Banner tone="warn">{t("oauth.no_handoff")}</Banner>
        <div>
          <Button onClick={() => navigate("/migrations/new")}>{t("oauth.start_again")}</Button>
        </div>
      </ReturnPage>
    );
  }

  if (handoff.isPending) return <Spinner />;

  if (handoff.isError || handoff.data === undefined) {
    // §A6.4 answers 404 for a consumed, expired or wrong-operator handoff and says nothing more; so do we.
    return (
      <ReturnPage sourceSystem="xero" title={t("oauth.return_title")}>
        <Banner tone="warn">{t("oauth.state_invalid")}</Banner>
        <div>
          <Button onClick={() => navigate("/migrations/new")}>{t("oauth.start_again")}</Button>
        </div>
      </ReturnPage>
    );
  }

  const tenants = handoff.data.tenants;
  const missing = handoff.data.missing_scopes ?? [];

  async function onReconsent(): Promise<void> {
    const draft = readDraft();
    const authorization = await authorize.mutateAsync({});
    saveDraft(draft);
    window.location.assign(authorization.authorization_url);
  }

  async function onSelect(): Promise<void> {
    if (tenantId === null) return;
    const draft = readDraft();
    const name = displayName.trim();
    try {
      const connection = await select.mutateAsync({
        tenant_id: tenantId,
        ...(name === "" ? {} : { display_name: name }),
      });
      const destinationId = draft.destination_connection_id;
      const returnTo = draft.return_to;
      clearDraft();
      if (destinationId === undefined) {
        if (returnTo === "/connections") {
          // §P2: the standalone "Add connection" flow has no destination in flight — back to the list, not
          // the migration wizard, with the same success flash a manual LuciBook connection gets.
          toast.push(t("connections.xero_connected"));
          navigate("/connections");
          return;
        }
        // No destination was chosen before the round trip: back to the connect step, source already made.
        navigate("/migrations/new");
        return;
      }
      navigate("/migrations/new/consent", {
        state: {
          source_system: connection.system,
          // §B1.5/Package W2c: the destination system travels in the draft alongside its connection id,
          // written before the redirect. `lucibook` is the fallback only for a draft written before this key
          // existed (or corrupted/dropped sessionStorage) — never a live guess.
          destination_system: draft.destination_system ?? "lucibook",
          source_connection_id: connection.id,
          destination_connection_id: destinationId,
        },
      });
    } catch (caught) {
      setFailure(describeApiError(caught, t("error.unexpected")));
    }
  }

  return (
    <ReturnPage sourceSystem="xero" eyebrow={t("oauth.return_eyebrow")} title={t("oauth.return_title")}>
      {missing.length === 0 ? null : (
        <Banner tone="warn">
          <p>
            {t("oauth.missing_scopes", { scopes: "" })}
            <span className="mono">{missing.join(", ")}</span>
          </p>
          <div>
            <Button size="compact" className="banner__action" onClick={() => void onReconsent()}>
              {t("oauth.reconsent")}
            </Button>
          </div>
        </Banner>
      )}
      {failure === null ? null : <Banner tone="block">{failure}</Banner>}
      <fieldset className="picklist">
        <legend className="returnpage__legend">{t("oauth.tenant_legend")}</legend>
        {tenants.map((tenant) => (
          <label key={tenant.tenant_id} className="pick pick--plain">
            <input
              type="radio"
              name="tenant"
              value={tenant.tenant_id}
              checked={tenantId === tenant.tenant_id}
              onChange={() => setTenantId(tenant.tenant_id)}
              /* The full "{name} ({id})" stays the accessible name; visually the id sits on the right. */
              aria-label={t("oauth.tenant_option", { name: tenant.tenant_name, id: tenant.tenant_id })}
            />
            <span className="pick__name">{tenant.tenant_name}</span>
            <span className="pick__meta">{tenant.tenant_id}</span>
          </label>
        ))}
      </fieldset>
      <Field label={t("oauth.name_label")} hint={t("oauth.name_hint")}>
        {({ id, describedBy }) => (
          <input
            id={id}
            type="text"
            aria-describedby={describedBy}
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
          />
        )}
      </Field>
      <div className="formactions formactions--start">
        <Button
          variant="primary"
          onClick={() => void onSelect()}
          disabled={tenantId === null || select.isPending}
        >
          {t("oauth.select")}
        </Button>
        <Link className="button" to="/connections">
          {t("oauth.start_again")}
        </Link>
      </div>
    </ReturnPage>
  );
}
